import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const stats = [
  { value: 80, suffix: '%', prefix: 'Tot ', label: 'Besparing op inkoop', icon: 'ri-money-euro-circle-line' },
  { value: 24, suffix: 'u', label: 'Reactietijd', icon: 'ri-time-line' },
  { value: 100, suffix: '+', label: 'Producten geleverd', icon: 'ri-box-3-line' },
  { value: 98, suffix: '%', label: 'Klanttevredenheid', icon: 'ri-thumb-up-line' },
];

const clients = ['Horeca', 'Webshops', 'Retail', 'Groothandel', 'Verpakkingen', 'Private Label', 'Maatwerk', 'Import'];

function useCountUp(target: number, duration = 1600, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, active]);
  return count;
}

function StatCard({ stat, index, active }: { stat: typeof stats[0]; index: number; active: boolean }) {
  const count = useCountUp(stat.value, 1600, active);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={active ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.1 }} className="flex flex-col items-center text-center px-4 py-6">
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3B9EE8]/12 mb-3">
        <i className={`${stat.icon} text-[#3B9EE8] text-lg`} />
      </div>
      <div className="font-heading font-800 text-[#0A1628] leading-none mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}>
        {stat.prefix || ''}{count}{stat.suffix}
      </div>
      <div className="w-8 h-0.5 bg-[#3B9EE8] rounded-full my-2" />
      <div className="text-[#0A1628]/55 text-xs font-500">{stat.label}</div>
    </motion.div>
  );
}

export default function TrustIndicators() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section ref={ref} className="bg-white py-12 md:py-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4 }} className="flex justify-center mb-8">
          <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/8 rounded-full px-3 py-1.5 text-[#3B9EE8] text-xs font-600">
            <i className="ri-verified-badge-line" />
            Bewezen resultaten
          </span>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mb-10">
          {stats.map((stat, i) => (
            <div key={stat.label} className="relative">
              <StatCard stat={stat} index={i} active={inView} />
              {i < stats.length - 1 && <div className="absolute right-0 top-1/4 h-1/2 w-px bg-[#0A1628]/6 hidden md:block" />}
            </div>
          ))}
        </div>

        <div className="border-t border-[#0A1628]/6 pt-8">
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.4, delay: 0.5 }} className="text-center text-[#0A1628]/35 text-xs uppercase tracking-widest mb-5">
            Wij leveren aan bedrijven in alle sectoren
          </motion.p>
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
            {clients.map((client, i) => (
              <motion.div key={client} initial={{ opacity: 0 }} animate={inView ? { opacity: 0.45 } : {}} whileHover={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.6 + i * 0.05 }} className="font-heading font-700 text-[#0A1628] text-sm tracking-tight cursor-default">
                {client}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
